import React, { useState, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import { Alert } from '@material-ui/lab';
import Compressor from 'compressorjs';
import clsx from 'clsx';
import pluralize from 'pluralize';
import Countdown from 'react-countdown';

import { openModal } from 'features/utilsSlice';
import { postRequest } from 'utils/api';
import { setGig } from 'features/gigsSlice';
import { getGigOwner, isAutomaticGG } from 'utils/helpers';
import UploadViewsDialog from './UploadViewsDialog';
import FullScreenshotDialog from './FullScreenshotDialog';
import ViewsDialog from './ViewsDialog';
import AddIcon from 'public/icons/add-main-bold.svg';
import SearchIcon from 'public/icons/search.svg';
import DisplayAsset from 'components/DisplayAsset';
import styles from 'styles/components/milestones/Milestone.module.scss';

const displayNone = { display: 'none' };
const padding = {
  padding: '20px 15px',
  width: '90%',
  margin: '0 auto',
  marginTop: 10,
  backgroundColor: '#ff4b4b',
  color: '#fff',
};
const opacity = { opacity: 0.4 };

const MilestoneScreenshots = ({
  milestone,
  overrideGig,
  handleUpdateMilestone,
  agencyView = false,
}) => {
  const [progress, setProgress] = useState(100);
  const [deliveryReady, setDeliveryReady] = useState(false);
  const stateGig = useSelector(state => state.gigs.mapping[milestone.gig_id]);
  const currentUser = useSelector(state => state.auth.currentUser);
  const gig = overrideGig || stateGig;
  const cancelledGig = gig?.status === 'cancelled';
  const router = useRouter();
  const gigOwner = agencyView
    ? router.pathname === '/dashboard/awaiting-approval'
    : getGigOwner(currentUser, gig?.user_id);
  const dispatch = useDispatch();
  const initialRef = useRef();
  const deliveryRef = useRef();
  const contentRef = useRef();
  const viewsRef = useRef();
  const disabled = gig?.status === 'completed';
  const noScreenshots =
    gigOwner &&
    !milestone.initial_screenshot.url &&
    !milestone.delivery_screenshot.url &&
    milestone.active;

  const getScreenshot = screenshot => !screenshot && gigOwner;
  const readyForDelivery =
    deliveryReady ||
    gig?.profile?.allow_early_delivery ||
    new Date() >= new Date(milestone?.expires_at);

  const automaticGG = isAutomaticGG(gig || {});

  const handleFileClick = ref => (!cancelledGig ? ref.current?.click() : null);

  const getRef = name => {
    if (name === 'initial_screenshot') {
      return initialRef;
    } else if (name === 'delivery_screenshot') {
      return deliveryRef;
    } else if (name === 'content') {
      return contentRef;
    }

    return viewsRef;
  };

  const handleClick = name => handleFileClick(getRef(name));

  const openFullScreenshotDialog = screenshot => {
    dispatch(openModal({ name: 'Full Screenshot', data: { milestone, screenshot } }));
  };

  const openViewsDialog = () => {
    handleFileClick(deliveryRef);

    if (milestone.inventory_space.has_views) dispatch(openModal({ name: 'Upload Views' }));
  };

  const handleAttachViews = () => handleFileClick(viewsRef);

  const getGGTitle = () => {
    if (milestone.guaranteed_gains && !automaticGG) {
      return 'Buyer uploads screenshot of the invite';
    } else if (milestone.guaranteed_gains && automaticGG) {
      return 'Delivery screenshot';
    } else return 'Add delivery screenshot';
  };

  const saveMilestoneScreenshot = (file, name) => {
    if (!file) return;

    setProgress(0);

    new Compressor(file, {
      quality: 0.8,
      success: compressedFile => {
        const data = new FormData();
        data.append(`milestone[${name}]`, compressedFile, compressedFile.name);

        postRequest({
          endpoint: `milestones/${milestone.id}/${name}`,
          data,
          errorMessage: 'Failed to upload screenshot',
          successMessage: 'Upload proccessed successfully',
          config: {
            onUploadProgress: progressEvent => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setProgress(percentCompleted);
            },
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        })
          .then(response => {
            if (overrideGig && handleUpdateMilestone) {
              handleUpdateMilestone(response.data);
            } else {
              const milestones = gig.milestones.map(m =>
                m.id === milestone.id ? response.data : m
              );

              dispatch(setGig({ ...gig, milestones }));
            }

            setProgress(100);

            if (name === 'views_screenshot') {
              dispatch(openModal({ name: 'Views' }));
            }
          })
          .catch(() => {});
      },
    });
  };

  const saveContentScreenshoot = (file, name) => {
    if (!file) return;

    setProgress(0);

    const data = new FormData();
    data.append(`milestone[${name}]`, file);

    postRequest({
      endpoint: `milestones/${milestone.id}/${name}`,
      data,
      errorMessage: 'Failed to upload content',
      successMessage: 'Upload proccessed successfully',
      config: {
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    })
      .then(response => {
        if (overrideGig && handleUpdateMilestone) {
          handleUpdateMilestone(response.data);
        } else {
          const milestones = gig.milestones.map(m => (m.id === milestone.id ? response.data : m));

          dispatch(setGig({ ...gig, milestones }));
        }

        setProgress(100);
      })
      .catch(() => {});
  };

  const displayInitialScreenshot =
    !milestone?.inventory_space?.event_attendance &&
    !milestone.inventory_space.license &&
    !milestone.inventory_space.has_content;

  const displayDeliveryScreenshot =
    (milestone.initial_screenshot.url ||
      milestone?.inventory_space?.event_attendance ||
      milestone?.inventory_space?.license) &&
    !milestone.inventory_space.has_content;

  return (
    <>
      <div className={styles.assetsImages} style={noScreenshots ? { width: '100%' } : {}}>
        {noScreenshots && (
          <Alert severity="info">
            Influencer has not started the campaign. You will get a notification when they do.
          </Alert>
        )}

        {displayInitialScreenshot && (
          <div
            onClick={() =>
              milestone.initial_screenshot.url
                ? openFullScreenshotDialog('initial_screenshot')
                : handleFileClick(initialRef)
            }
            className={styles.screenshot}
            style={getScreenshot(milestone.initial_screenshot.url) ? displayNone : {}}
          >
            {milestone.initial_screenshot.url ? (
              <div className={styles.addAsset}>
                <div className={styles.shadow} />
                <Image
                  layout="fill"
                  src={milestone.initial_screenshot.url}
                  alt="Initial Screenshot"
                  unoptimized
                />
                <SearchIcon className={styles.searchIcon} />
              </div>
            ) : (
              <div className={styles.addAsset} style={cancelledGig ? { opacity: 0.5 } : {}}>
                <AddIcon width={24} height={24} alt="Add Screenshot" />
              </div>
            )}

            <span>Proof of post</span>
          </div>
        )}

        {displayDeliveryScreenshot && (
          <div
            onClick={() => {
              if (
                (!readyForDelivery && !milestone?.inventory_space?.event_attendance) ||
                (!milestone.delivery_screenshot.url && !gigOwner && milestone.guaranteed_gains)
              ) {
                return null;
              } else {
                milestone.delivery_screenshot.url
                  ? openFullScreenshotDialog('delivery_screenshot')
                  : openViewsDialog();
              }
            }}
            className={clsx(styles.screenshot, { [styles.disabled]: !readyForDelivery })}
            style={getScreenshot(milestone.delivery_screenshot.url) ? displayNone : {}}
          >
            {milestone.delivery_screenshot.url ? (
              <div className={styles.addAsset}>
                <div className={styles.shadow} />

                <Image
                  layout="fill"
                  src={milestone.delivery_screenshot.url}
                  alt="Delivery Screenshot"
                  unoptimized
                />

                <SearchIcon className={styles.searchIcon} />
              </div>
            ) : (
              <div className={styles.addAsset}>
                {!readyForDelivery && !milestone.guaranteed_gains ? (
                  <div style={{ position: 'absolute', top: 10, color: '#5b00db' }}>
                    <span
                      style={{
                        fontSize: 24,
                        fontFamily: 'Avenir Heavy',
                      }}
                    >
                      <Countdown
                        date={new Date(milestone.expires_at)}
                        intervalDelay={60000}
                        renderer={({ days, hours, minutes }) => {
                          if (days >= 2 && hours >= 0) {
                            return `${days} days ${
                              hours > 0 ? `${hours} ${pluralize('hours', hours)}` : ''
                            } `;
                          } else if (hours >= 0 && days < 2) {
                            const dayToHours = days * 24;
                            return `${hours + dayToHours} ${pluralize('hours', hours)}`;
                          } else if (minutes > 0 && hours === 0) {
                            return `${minutes} ${pluralize('minutes', minutes)}`;
                          } else {
                            return 0;
                          }
                        }}
                        onComplete={() => setDeliveryReady(true)}
                      />
                    </span>
                    <span style={{ fontSize: 16, opacity: 0.9 }}>Until..</span>
                  </div>
                ) : (
                  <div style={milestone.guaranteed_gains ? { opacity: 0.2 } : {}}>
                    <AddIcon width={24} height={24} alt="Add Screenshot" />
                  </div>
                )}
              </div>
            )}

            {milestone.delivery_screenshot.url ? (
              <span>
                {milestone.inventory_space.license
                  ? milestone.inventory_space.name
                  : 'Delivery screenshot'}
              </span>
            ) : (
              <span style={!readyForDelivery ? opacity : {}}>{getGGTitle()}</span>
            )}
          </div>
        )}

        {milestone?.inventory_space?.has_content && (
          <div
            onClick={() =>
              milestone.content.url
                ? openFullScreenshotDialog('content')
                : handleFileClick(contentRef)
            }
            className={styles.screenshot}
            style={getScreenshot(milestone.content.url) ? displayNone : {}}
          >
            {milestone.content.url ? (
              <div className={styles.addAsset}>
                <div className={styles.shadow} />

                <div
                  style={
                    milestone.content.content_type.includes('audio') ? { position: 'absolute' } : {}
                  }
                  className={clsx({
                    [styles.videoPosition]: milestone.content.content_type.includes('video'),
                  })}
                >
                  <DisplayAsset asset={milestone.content} />
                </div>

                <SearchIcon className={styles.searchIcon} />
              </div>
            ) : (
              <div className={styles.addAsset}>
                <AddIcon width={24} height={24} alt="Add Screenshot" />
              </div>
            )}

            <span>{milestone.content.url ? 'View content' : 'Upload content/proof'}</span>
          </div>
        )}

        {milestone.delivery_screenshot.url && milestone.inventory_space.has_views && (
          <div
            onClick={() => {
              milestone.views_screenshot.url
                ? openFullScreenshotDialog('views_screenshot')
                : handleFileClick(viewsRef);
            }}
            className={styles.screenshot}
          >
            {milestone.views_screenshot.url ? (
              <div className={styles.addAsset}>
                <div className={styles.shadow} />
                <Image
                  layout="fill"
                  src={milestone.views_screenshot.url}
                  alt="Views Screenshot"
                  unoptimized
                />
                <SearchIcon className={styles.searchIcon} />
              </div>
            ) : (
              <div className={styles.addAsset}>
                <AddIcon width={24} height={24} alt="Add Screenshot" />
              </div>
            )}

            {milestone.views_screenshot.url ? (
              <span>Views screenshot</span>
            ) : (
              <span>Add views screenshot</span>
            )}
          </div>
        )}
      </div>

      {milestone.status === 'rejected' && milestone.rejection_reason && (
        <Alert severity="error" style={padding} icon={false}>
          {milestone.rejection_reason}
        </Alert>
      )}

      <FullScreenshotDialog
        milestone={milestone}
        disabled={disabled}
        handleClick={handleClick}
        progress={progress}
      />

      {milestone.delivery_screenshot.url && !milestone.views_screenshot.url && (
        <UploadViewsDialog milestone={milestone} handleAttachViews={handleAttachViews} />
      )}

      {milestone.views_screenshot.url && (
        <ViewsDialog milestone={milestone} handleAttachViews={handleAttachViews} gig={gig} />
      )}

      <input
        ref={initialRef}
        type="file"
        name="initial_screenshot"
        onChange={e => saveMilestoneScreenshot(e.target.files[0], 'initial_screenshot')}
        accept="image/jpg, image/jpeg, image/png"
      />

      <input
        ref={deliveryRef}
        type="file"
        name="delivery_screenshot"
        onChange={e => saveMilestoneScreenshot(e.target.files[0], 'delivery_screenshot')}
        accept="image/jpg, image/jpeg, image/png"
      />

      <input
        ref={viewsRef}
        type="file"
        name="views_screenshot"
        onChange={e => saveMilestoneScreenshot(e.target.files[0], 'views_screenshot')}
        accept="image/jpg, image/jpeg, image/png"
      />

      <input
        ref={contentRef}
        type="file"
        name="content"
        onChange={e => saveContentScreenshoot(e.target.files[0], 'content')}
        accept="image/jpg, image/jpeg, image/png, audio/*, video/*, audio/ogg"
      />
    </>
  );
};

export default MilestoneScreenshots;
